import yup from "yup";
import locale from "@bonliva-crm/locale";
import { WeekYear } from "@bonliva-crm/common";
import { GetAccommodationConfirmationByIdQuery } from "@graphql";
import { getWeekStayedPeriod } from "./erp-week-stayed-period";

export interface AccommodationConfirmationFormData {
  files: File[];
  accommodationId: string;
  offerId: string;
  totalCost: number;
  totalExVAT?: boolean;
  weeks: AccommodationWeek[];
  city: string;
  area: string;
  squareFootageAttached?: boolean;
  /** Row origin — "legacy" (CRM-authored) or "erp" (read-only ERP mirror). */
  source: string;
  /** ERP taxation id — set only on "erp"-sourced rows; powers the ERP deep-link. */
  taxationId?: string | null;
  /** When the ERP mirror last synced — set only on "erp"-sourced rows. */
  syncedAt?: Date | null;
}

export interface AccommodationWeek {
  weekYear: WeekYear;
  isTaxed: boolean;
  cost: string;
  extraCost: string;
  extraDescription: string;
  startDate: Date;
  endDate: Date;
  nights: string;
  tax: number;
  note: string;
  /** ERP reservation lines, read-only; empty for CRM-authored weeks. */
  lines?: AccommodationWeekLine[];
}

export type AccommodationWeekLine =
  GetAccommodationConfirmationByIdQuery["accommodationConfirmationById"]["allWeeks"][number]["lines"][number];

export const validationSchema = yup.object<AccommodationConfirmationFormData>({
  files: yup.array(),
  weeks: yup
    .array(
      yup.object<Partial<AccommodationWeek>>({
        isTaxed: yup.boolean(),
        startDate: yup
          .date()
          .nullable(true)
          .when("isTaxed", {
            is: (v) => !!v,
            then: (s) => s.required(locale.StartDate)
          }),
        endDate: yup
          .date()
          .nullable(true)
          .when("isTaxed", {
            is: (v) => !!v,
            then: (s) => s.required(locale.EndDate)
          }),
        extraCost: yup.mixed(),
        extraDescription: yup
          .string()
          .nullable(true)
          .when("extraCost", {
            is: (v) => {
              return v && v !== "0" && parseInt(v) > 0;
            },
            then: (s) => s.required(locale.fieldIsMandatory("Beskrivning"))
          })
      })
    )
    .min(1, locale.Period) as yup.Schema<any>,
  accommodationId: yup.string(),
  offerId: yup.string().nullable(true),
  area: yup.string(),
  city: yup.string().required("Ort är obligatoriskt, välj övrigt ifall en ort saknas"),
  totalCost: yup.number().nullable(true),
  squareFootageAttached: yup.boolean().nullable(true),
  source: yup.string(),
  taxationId: yup.string().nullable(true),
  syncedAt: yup.date().nullable(true)
});

export function mapDataToValues(
  data: GetAccommodationConfirmationByIdQuery
): AccommodationConfirmationFormData {
  return {
    area: data.accommodationConfirmationById.area.toString(),
    city: data.accommodationConfirmationById.city,
    accommodationId: data.accommodationConfirmationById.accommodationRef?.NodeID,
    files: [],
    offerId: data.accommodationConfirmationById.offerId,
    totalCost: data.accommodationConfirmationById.totalCost,
    totalExVAT: data.accommodationConfirmationById.totalExVAT ?? false,
    weeks: data.accommodationConfirmationById.allWeeks.map((x) => {
      const stayed = getWeekStayedPeriod(x);
      return {
        weekYear: x.weekYear,
        tax: x.tax,
        cost: x.cost.toString(),
        extraCost: x.extraCost.toString(),
        extraDescription: x.extraDescription,
        startDate: x.startDate,
        endDate: stayed?.endDate ?? x.endDate,
        nights: (stayed?.nights ?? x.nights).toString(),
        isTaxed: x.isTaxed,
        note: x.note,
        lines: x.lines ?? []
      };
    }),
    squareFootageAttached: data.accommodationConfirmationById.squareFootageAttached,
    source: data.accommodationConfirmationById.source,
    taxationId: data.accommodationConfirmationById.taxationId,
    syncedAt: data.accommodationConfirmationById.syncedAt
  };
}
